import {
  faGrimace as emptySadFace4,
  faSadTear as emptySadFace3,
  faDizzy as emptySadFace2,
  faFrownOpen as emptySadFace1,
  faMeh as emptyNeutralFace,
  faGrin as emptyHappyFace1,
  faSmileBeam as emptyHappyFace2,
  faKissWinkHeart as emptyHappyFace3,
  faGrinStars as emptyHappyFace4,
  faGrinBeam as emptyHappyFace5,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faGrimace as fullSadFace4,
  faSadTear as fullSadFace3,
  faDizzy as fullSadFace2,
  faFrownOpen as fullSadFace1,
  faMeh as fullNeutralFace,
  faGrin as fullHappyFace1,
  faSmileBeam as fullHappyFace2,
  faKissWinkHeart as fullHappyFace3,
  faGrinStars as fullHappyFace4,
  faGrinBeam as fullHappyFace5,
} from '@fortawesome/pro-solid-svg-icons';

import questionTypes from '../questionTypes';
import createEmptyOptionCollection from '../../utils/createEmptyOptionCollection';
import personalLabelMap from '../../config/personalLabelMap';
import segmentLabelMap from '../../config/segmentLabelMap';
import numbering from '../../utils/numbering';
import getCommonBlockProps, { commentsProps } from './getCommonBlockProps';
import { DEFAULT_COL_LABEL } from './constants';
import { STYLING_KEY_QUESTION_TEXT } from '../../constants/stylingKey';

const { labelListEndMidpoint, labelListEnd, itemList } = segmentLabelMap;

const highlightColorsMap = {
  'red-2': { borderColor: '#DB3545', backgroundColor: '#FCEBEB' },
  'red-1': { borderColor: '#E16944', backgroundColor: 'rgba(225, 103, 68, 0.15)' },
  'orange-2': { borderColor: '#E48843', backgroundColor: 'rgba(228, 138, 67, 0.15)' },
  'orange-1': { borderColor: '#E8AC42', backgroundColor: 'rgba(232, 174, 66, 0.15)' },
  'yellow-2': { borderColor: '#ECD041', backgroundColor: 'rgba(236, 209, 65, 0.15)' },
  'yellow-1': { borderColor: '#EEF341', backgroundColor: 'rgba(239, 243, 65, 0.15)' },
  'light-green-1': {
    borderColor: '#C2E858',
    backgroundColor: 'rgba(194, 232, 88, 0.15)',
  },
  'light-green-2': {
    borderColor: '#98DE6E',
    backgroundColor: 'rgba(151, 221, 111, 0.15)',
  },
  'green-1': { borderColor: '#6BD285', backgroundColor: 'rgba(109, 211, 133, 0.15)' },
  'green-2': { borderColor: '#2EC3A5', backgroundColor: 'rgba(64, 200, 156, 0.15)' },
  'green-3': { borderColor: '#17A589', backgroundColor: 'rgba(23, 165, 137, 0.15)' },
};

export const highlightColorsList = {
  3: [highlightColorsMap['red-2'], highlightColorsMap['yellow-1'], highlightColorsMap['green-2']],
  4: [
    highlightColorsMap['red-2'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-2'],
  ],
  5: [
    highlightColorsMap['red-2'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['yellow-2'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-2'],
  ],
  6: [
    highlightColorsMap['red-2'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['yellow-2'],
    highlightColorsMap['yellow-1'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-2'],
  ],
  7: [
    highlightColorsMap['red-2'],
    highlightColorsMap['red-1'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['yellow-2'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-1'],
    highlightColorsMap['green-2'],
  ],
  8: [
    highlightColorsMap['red-2'],
    highlightColorsMap['red-1'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['yellow-2'],
    highlightColorsMap['yellow-1'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-1'],
    highlightColorsMap['green-2'],
  ],
  9: [
    highlightColorsMap['red-2'],
    highlightColorsMap['red-1'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['orange-1'],
    highlightColorsMap['yellow-2'],
    highlightColorsMap['light-green-1'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-1'],
    highlightColorsMap['green-2'],
  ],
  10: [
    highlightColorsMap['red-2'],
    highlightColorsMap['red-1'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['orange-1'],
    highlightColorsMap['yellow-2'],
    highlightColorsMap['yellow-1'],
    highlightColorsMap['light-green-1'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-1'],
    highlightColorsMap['green-2'],
  ],
  11: [
    highlightColorsMap['red-2'],
    highlightColorsMap['red-1'],
    highlightColorsMap['orange-2'],
    highlightColorsMap['orange-1'],
    highlightColorsMap['yellow-2'],
    highlightColorsMap['yellow-1'],
    highlightColorsMap['light-green-1'],
    highlightColorsMap['light-green-2'],
    highlightColorsMap['green-1'],
    highlightColorsMap['green-2'],
    highlightColorsMap['green-3'],
  ],
};

export const smileyFacesMap = {
  'sad-1': { empty: emptySadFace1, full: fullSadFace1 },
  'sad-2': { empty: emptySadFace2, full: fullSadFace2 },
  'sad-3': { empty: emptySadFace3, full: fullSadFace3 },
  'sad-4': { empty: emptySadFace4, full: fullSadFace4 },
  neutral: { empty: emptyNeutralFace, full: fullNeutralFace },
  'happy-1': { empty: emptyHappyFace1, full: fullHappyFace1 },
  'happy-2': { empty: emptyHappyFace2, full: fullHappyFace2 },
  'happy-3': { empty: emptyHappyFace3, full: fullHappyFace3 },
  'happy-4': { empty: emptyHappyFace4, full: fullHappyFace4 },
  'happy-5': { empty: emptyHappyFace5, full: fullHappyFace5 },
};

export const smileyFacesList = {
  3: [smileyFacesMap['sad-1'], smileyFacesMap['neutral'], smileyFacesMap['happy-1']],
  4: [
    smileyFacesMap['sad-1'],
    smileyFacesMap['neutral'],
    smileyFacesMap['happy-1'],
    smileyFacesMap['happy-2'],
  ],
  5: [
    smileyFacesMap['sad-2'],
    smileyFacesMap['sad-1'],
    smileyFacesMap['neutral'],
    smileyFacesMap['happy-1'],
    smileyFacesMap['happy-4'],
  ],
  6: [
    smileyFacesMap['sad-2'],
    smileyFacesMap['sad-1'],
    smileyFacesMap['neutral'],
    smileyFacesMap['happy-1'],
    smileyFacesMap['happy-2'],
    smileyFacesMap['happy-4'],
  ],
  7: [
    smileyFacesMap['sad-3'],
    smileyFacesMap['sad-2'],
    smileyFacesMap['sad-1'],
    smileyFacesMap['neutral'],
    smileyFacesMap['happy-1'],
    smileyFacesMap['happy-2'],
    smileyFacesMap['happy-4'],
  ],
  8: [
    smileyFacesMap['sad-3'],
    smileyFacesMap['sad-2'],
    smileyFacesMap['sad-1'],
    smileyFacesMap['neutral'],
    smileyFacesMap['happy-1'],
    smileyFacesMap['happy-2'],
    smileyFacesMap['happy-3'],
    smileyFacesMap['happy-4'],
  ],
  9: [
    smileyFacesMap['sad-4'],
    smileyFacesMap['sad-3'],
    smileyFacesMap['sad-2'],
    smileyFacesMap['sad-1'],
    smileyFacesMap['neutral'],
    smileyFacesMap['happy-1'],
    smileyFacesMap['happy-2'],
    smileyFacesMap['happy-3'],
    smileyFacesMap['happy-4'],
  ],
  10: [
    smileyFacesMap['sad-4'],
    smileyFacesMap['sad-3'],
    smileyFacesMap['sad-2'],
    smileyFacesMap['sad-1'],
    smileyFacesMap['neutral'],
    smileyFacesMap['happy-1'],
    smileyFacesMap['happy-2'],
    smileyFacesMap['happy-3'],
    smileyFacesMap['happy-4'],
    smileyFacesMap['happy-5'],
  ],
};

export const shapeList = [
  {
    value: 'checkmark',
    label: 'Checkmarks',
    classNameList: {
      empty: 'icon-custom-circle-check',
      full: 'icon-custom-check-fill',
      fullSubstrate: 'icon-custom-circle-fill',
    },
  },
  {
    value: 'circle',
    label: 'Circles',
    classNameList: {
      empty: 'icon-custom-circle',
      full: 'icon-custom-circle-fill',
    },
  },
  {
    value: 'heart',
    label: 'Hearts',
    classNameList: {
      empty: 'icon-custom-heart',
      full: 'icon-custom-heart-fill',
    },
  },
  {
    value: 'smile',
    label: 'Smiley faces',
    classNameList: {
      empty: 'icon-custom-smiley',
      full: 'icon-custom-smiley-fill',
      fullSubstrate: 'icon-custom-circle-fill',
    },
  },
  {
    value: 'star',
    label: 'Stars',
    classNameList: {
      empty: 'icon-custom-star',
      full: 'icon-custom-star-fill',
    },
  },
  {
    value: 'thumbup',
    label: 'Thumbs-up',
    classNameList: {
      empty: 'icon-custom-thumbs-up',
      full: 'icon-custom-thumbs-up-fill',
    },
  },
];

export const sizeList = [
  {
    value: 'small',
    label: 'Small',
    size: 16,
  },
  {
    value: 'medium',
    label: 'Medium',
    size: 21,
  },
  {
    value: 'large',
    label: 'Large',
    size: 26,
  },
];

export const defaultScaleSize = 'small';
export const initialRate = null;
const endMidpointLabelList = ['Bad', 'Good enough', 'Awesome'];
const endLabelList = ['Bad', 'Awesome'];

const getDefaultProps = () => ({
  ...getCommonBlockProps(),
  ...commentsProps,
  enableFillColor: false,
  size: defaultScaleSize,
  shape: 'star',
  personalLabelType: personalLabelMap.noLabel,
  segment: labelListEnd,
  [itemList]: createEmptyOptionCollection(5, DEFAULT_COL_LABEL),
  [labelListEnd]: createEmptyOptionCollection(endLabelList.length, endLabelList),
  [labelListEndMidpoint]: createEmptyOptionCollection(endMidpointLabelList.length, endMidpointLabelList),
});

const pickBlockPropsToInitialValues = ({
  color = null,
  commentsEnabled = null,
  itemList = [],
  logic: { isEnabled = null } = {},
  personalLabelType = null,
  required = null,
  segment = null,
  enableFillColor = false,
  shape = null,
  size = null,
  type = null,
}) => ({
  color,
  commentsEnabled,
  itemList,
  logic: { isEnabled },
  personalLabelType,
  required,
  segment,
  enableFillColor,
  shape,
  size,
  type,
});

export default {
  type: questionTypes.questionScale,
  getDefaultProps,
  pickBlockPropsToInitialValues,
  limits: {
    itemList: {
      max: 10,
      min: 3,
    },
  },
  settings: {
    defaultStylingEntity: STYLING_KEY_QUESTION_TEXT,
    isRequiredField: () => false,
    isUniqueField: () => false,
    isTextRequired: () => true,
  },
  toLogicOptions: (questions) =>
    Array.from({ length: questions.itemList.length }, (option, index) => {
      const value = numbering.toHuman(index);

      return {
        value,
        label: value,
        shape: questions.shape,
        color: questions.color,
      };
    }),
};
